import { COMPONENT_PLACEMENT_FRONT_AND_TOP, type Blueprint, type Island, type Kitchen } from '@rta/rta-blueprint-library';

import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelCard } from '../shared/blueprint-panel-card';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { EmbeddedSvgViewport } from '../shared/embedded-svg-viewport';
import { getCountertopNotes } from '../helpers/get-countertop-notes';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { hasLevelingLegs, LEVELING_LEGS_PANEL_NOTE } from '../helpers/has-leveling-legs';
import { getPageOverrideScope } from '../helpers/get-page-override-scope';
import { PAGE_BODY_PADDING } from '../shared/blueprint-view-styles';
import type { BlueprintPage } from '../types';

export type BlueprintPanelPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

const countOutletsOnPanels = (island: Island, panelFilter: (p: { isFrontPanel: boolean; isSidePanel: boolean; isBackPanel: boolean }) => boolean): number => {
	return island.panels.children
		.filter(panelFilter)
		.reduce((sum, panel) => sum + panel.panelItemGroups.children
			.reduce((groupSum, group) => groupSum + group.items.children
				.filter((pi) => (pi.item?.category?.isOutlet || (pi.item?.sku ?? '').toUpperCase().includes('OUTLET') || (pi.item?.label ?? '').toUpperCase().includes('OUTLET')) && !(pi.item?.placement ?? '').toLowerCase().includes('top')).length, 0), 0);
};

const junctionNote = (count: number): string => count > 1
	? 'Please Note: Junction boxes provided, outlets to be supplied by customer.'
	: 'Please Note: Junction box provided, outlet to be supplied by customer.';


const getFrontPanelWarmingDrawerSku = (island: Island): string | null => {
	const skus = island.panels.children
		.filter((p) => p.isFrontPanel)
		.flatMap((panel) => panel.panelItemGroups.children
			.flatMap((group) => group.items.children.map((pi) => pi.item?.sku ?? '')));
	return skus.find((sku) => sku === 'C3-CDWD' || sku === 'C3-WD') ?? null;
};

const getCountertopThicknessCutoutSkus = (island: Island): Array<string> => {
	return island.panels.children
		.filter((p) => p.isFrontPanel)
		.flatMap((panel) => panel.panelItemGroups.children
			.flatMap((group) => group.items.children
				.filter((pi) => pi.item?.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP)
				.map((pi) => pi.item?.sku ?? '')))
		.filter((sku) => sku.length > 0);
};

const LEVELING_LEGS_PAGE_TYPES = ['front-panel', 'side-panels', 'back-panel'];

const getNotesForPageType = (pageType: string, island: Island | null, kitchen: Kitchen | null): Array<string> => {
	const notes: Array<string> = [];

	switch (pageType) {
		case 'front-panel':
			notes.push('Please Note: This view is the exterior side of the panel');
			// The trigger is a split FRONT panel, not a countertop seam — a front panel broken into
			// sections loses its continuity, so the back is what needs reinforcing. Read off real
			// finalized BPs: Jaques islands B/C/D all carry front-panel breakpoints and the note
			// (C has NO countertop seam), while Tancredo's 168" island has a back-panel breakpoint
			// and a countertop with no seam, no front-panel break — and no note.
			if (island && (island.panels.getFrontPanel()?.breakpoints.children.length ?? 0) > 0) {
				notes.push('MFG: Please add additional support to back panel');
			}
			const frontOutlets = island ? countOutletsOnPanels(island, (p) => p.isFrontPanel) : 0;
			if (frontOutlets > 0) {
				notes.push(junctionNote(frontOutlets));
			}
			const warmingDrawerSku = island ? getFrontPanelWarmingDrawerSku(island) : null;
			if (warmingDrawerSku) {
				notes.push(`MFG: Please include aluminum channel on all four sides of ${warmingDrawerSku} cutout`);
			}
			const isNoRtaCountertop = (island?.getCountertop()?.material?.key ?? '') === 'none';
			if (island && isNoRtaCountertop) {
				const thicknessCutoutSkus = getCountertopThicknessCutoutSkus(island);
				if (thicknessCutoutSkus.length > 0) {
					notes.push(`${thicknessCutoutSkus.join(' and ')} cutout height based on 1.25" countertop thickness`);
				}
			}
			break;
		case 'internal-supports':
		case 'side-panels':
			notes.push('Please Note: This view is the exterior side of the panels');
			const sideOutlets = island ? countOutletsOnPanels(island, (p) => p.isSidePanel) : 0;
			if (sideOutlets > 0) {
				notes.push(junctionNote(sideOutlets));
			}
			break;
		case 'back-panel':
			notes.push('Please Note: This view is the exterior side of the panel');
			const backOutlets = island ? countOutletsOnPanels(island, (p) => p.isBackPanel) : 0;
			if (backOutlets > 0) {
				notes.push(junctionNote(backOutlets));
			}
			break;
		case 'countertop':
			if (island && kitchen) {
				return getCountertopNotes({ island: island, kitchen });
			}
			break;
		default:
	}

	if (kitchen && LEVELING_LEGS_PAGE_TYPES.includes(pageType) && hasLevelingLegs({ kitchen, islandId: island?.id ?? null })) {
		notes.push(LEVELING_LEGS_PANEL_NOTE);
	}

	return notes;
};

export const BlueprintPanelPage = (props: BlueprintPanelPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const kitchen = blueprint.kitchens.getActiveVersion();
	const island = page.islandId ? kitchen?.design.islands.findById(page.islandId) : null;
	const notes = getNotesForPageType(page.pageType, island, kitchen);
	const isCountertop = page.pageType === 'countertop';

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			{/* Child 1: Header */}
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			{/* Child 2: Content area (flex: 1). clip-path forces clipping in
			    html2canvas export where overflow:hidden alone is not respected. */}
			<div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', clipPath: 'inset(0)', minHeight: 0 }}>
				<div style={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
					minHeight: 0,
					padding: PAGE_BODY_PADDING
				}}>
					{isCountertop ? (
						<EmbeddedSvgViewport
							blueprint={blueprint}
							islandId={page.islandId}
							panelId={null}
							displayMode={DisplayMode.Manufacturing}
							pageIndex={page.pageNumber}
						/>
					) : (
						<BlueprintPanelCard
							blueprint={blueprint}
							islandId={page.islandId}
							panelId={page.panelId}
							pageIndex={page.pageNumber}
						/>
					)}
				</div>
			</div>

			{/* Child 3: Combined panel footer + page footer (PDF containerPanelFooter gap 10pt -> 14px) */}
			<div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
				<BlueprintPanelFooter scope={getPageOverrideScope(page)} notes={notes} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
